<script lang="ts">
	import type { Component } from 'svelte';
	import { twMerge } from 'tailwind-merge';
	import { LinkIcon } from '../../../assets/icons/LinkIcon';
	import { MailIcon } from '../../../assets/icons/MailIcon';
	import { MessengerIcon } from '../../../assets/icons/MessengerIcon';
	import { TelegramIcon } from '../../../assets/icons/TelegramIcon';
	import { TextIcon } from '../../../assets/icons/TextIcon';
	import { TickIcon } from '../../../assets/icons/TickIcon';
	import { TwitterXIcon } from '../../../assets/icons/TwitterXIcon';
	import { WhatsappIcon } from '../../../assets/icons/WhatsappIcon';
	import { isMobileDevice } from '../../../utils/isMobileDevice';
	import { Link } from '../../Link';
	import { Txt } from '../../Txt';

	interface Props {
		dataCy: string;
		colored?: boolean | undefined;
		intro: string;
		link: string;
		showTwitter?: boolean;
		showText?: boolean;
		onClickPlatform: (platform: string) => void;
	}

	let {
		dataCy,
		colored = false,
		intro,
		link,
		showTwitter = true,
		showText = true,
		onClickPlatform
	}: Props = $props();

	let copied = $state(false);
	let [url, anchor] = $derived(link.split('#'));

	let platformPrefix = $derived(link.includes('?') ? '&' : '?');

	const objectToGetParams = (object: { [key: string]: string | number | undefined | null }) => {
		const params = Object.entries(object)
			.filter(([, value]) => value !== undefined && value !== null)
			.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);

		return params.length > 0 ? `?${params.join('&')}` : '';
	};

	let socials = $derived([
		...(showText
			? [
					{
						dataCy: 'text',
						name: 'Text',
						onClick: () => onClickPlatform('text'),
						href: `sms:&body=${encodeURIComponent(
							`${intro}\n\n${url}${platformPrefix}platform=text${anchor ? `#${anchor}` : ''}`
						)}`,
						Icon: TextIcon
					}
				]
			: []),
		{
			dataCy: 'whatsapp',
			name: 'Whatsapp',
			onClick: () => onClickPlatform('whatsapp'),
			href: (() => {
				return (
					'https://api.whatsapp.com/send' +
					objectToGetParams({
						text: `${intro}: ${url}${platformPrefix}platform=whatsapp${anchor ? `#${anchor}` : ''}`
					})
				);
			})(),
			...(colored && {
				class: '[&>path]:fill-[#25D366]'
			}),
			Icon: WhatsappIcon
		},
		{
			dataCy: 'email',
			name: 'Email',
			onClick: () => onClickPlatform('email'),
			href: `mailto:?to=&body=${encodeURIComponent(
				`${intro}\n\n${url}${platformPrefix}platform=email${anchor ? `#${anchor}` : ''}`
			)}`,
			Icon: MailIcon
		},
		...(showTwitter
			? [
					{
						dataCy: 'twitter',
						name: 'X (formerly Twitter)',
						onClick: () => onClickPlatform('twitter'),
						href: (() => {
							return (
								'https://twitter.com/intent/tweet' +
								objectToGetParams({
									url: `${url}${platformPrefix}platform=twitter${anchor ? `#${anchor}` : ''}`,
									text: intro,
									hashtags: 'HENI'
								})
							);
						})(),
						Icon: TwitterXIcon
					}
				]
			: []),
		{
			dataCy: 'messenger',
			name: 'Messenger',
			onClick: () => onClickPlatform('messenger'),
			href: (() => {
				if (isMobileDevice()) {
					return `fb-messenger://share/?link=${encodeURIComponent(
						`${url}${platformPrefix}platform=messenger${anchor ? `#${anchor}` : ''}`
					)}&app_id=353924379641181`;
				}

				return (
					'https://www.facebook.com/dialog/send' +
					objectToGetParams({
						link: `${url}${platformPrefix}platform=messenger${anchor ? `#${anchor}` : ''}`,
						redirect_uri: `${link}${platformPrefix}platform=messenger`,
						app_id: '353924379641181'
					})
				);
			})(),
			...(colored && {
				class: '[&>path]:fill-[#0084FF]'
			}),
			Icon: MessengerIcon
		},
		{
			dataCy: 'telegram',
			name: 'Telegram',
			onClick: () => onClickPlatform('telegram'),
			href: (() => {
				return (
					'https://telegram.me/share/url' +
					objectToGetParams({
						url: `${url}${platformPrefix}platform=telegram${anchor ? `#${anchor}` : ''}`,
						text: intro
					})
				);
			})(),
			...(colored && {
				class: '[&>g>path]:fill-[#00ADEC]'
			}),
			Icon: TelegramIcon
		},
		{
			dataCy: 'link',
			name: copied ? 'Copied' : 'Copy Link',
			onClick: () => {
				if (!copied) {
					navigator.clipboard.writeText(
						`${url}${platformPrefix}platform=link${anchor ? `#${anchor}` : ''}`
					);
					copied = true;

					setTimeout(() => {
						copied = false;
					}, 2000);

					onClickPlatform('link');
				}
			},
			Icon: copied ? TickIcon : LinkIcon,
			...(copied
				? {
						iconColor: 'green-600',
						classes: {
							link: '!border-green-600'
						}
					}
				: {})
		}
	] as {
		dataCy: string;
		name: string;
		href?: string;
		onClick?: () => void;
		Icon: Component;
		iconColor?: string;
		class?: string;
		classes?: { link?: string };
	}[]);
</script>

<div class="xs:grid-cols-3 grid grid-cols-2 justify-between">
	{#each socials as social}
		<div>
			<Link
				{...social.href && {
					href: social.href
				}}
				href={social.href}
				data-cy={`${dataCy}-${social.dataCy}`}
				target="_blank"
				onclick={social.onClick as () => void}
				rel="noopeneer noreferrer"
				class={twMerge(
					'mx-auto flex h-[3.5rem] w-[3.5rem] cursor-pointer items-center justify-center rounded border border-gray-200 hover:border-gray-700',
					social.classes?.link
				)}
			>
				<social.Icon class={social.class} color={social.iconColor} />
			</Link>
			<Txt variant="label2" class="mb-[2.5rem] mt-[0.625rem] text-center">{social.name}</Txt>
		</div>
	{/each}
</div>
